import type { WorldEventEnded, WorldEventState } from '@shefira/shared';

import { uiFormat, uiNumber, uiText } from './text.js';

/**
 * The words of each world event's scenario (ADR 0050), for the tracker at
 * the top of the screen and the result window: an invasion has waves and a
 * warlord, the giant is one creature, the cart has a road and its escorts.
 */

/** The tracker's line: what is coming or going on, and who is there. */
export function trackerLine(state: WorldEventState): string[] {
  const present =
    state.kind === 'convoy'
      ? uiFormat('{count} escorteurs', { count: state.defenders })
      : state.kind === 'giant'
        ? uiFormat('{count} combattants', { count: state.defenders })
        : uiFormat('{count} défenseurs', { count: state.defenders });
  if (state.phase === 'warning') {
    const coming =
      state.kind === 'convoy'
        ? uiText('Le chariot va partir')
        : state.kind === 'giant'
          ? uiText('Le géant approche')
          : uiText('Les assaillants approchent');
    return [coming, present];
  }
  if (state.kind === 'giant') return [uiText('Le géant est là !'), present];
  if (state.kind === 'convoy') {
    const progress = state.cart?.progress ?? 0;
    const road =
      state.cart?.rolling === true
        ? uiFormat('Route : {progress} % · le chariot avance', { progress })
        : uiFormat('Route : {progress} % · le chariot est arrêté', { progress });
    const waves = uiFormat('Vague {wave}/{waves} · {foes} assaillants', {
      wave: state.wave,
      waves: state.waves,
      foes: state.foes,
    });
    return [road, waves, present];
  }
  const wave = state.warlord
    ? uiFormat('Vague {wave}/{waves} · le chef de guerre est là !', {
        wave: state.wave,
        waves: state.waves,
      })
    : uiFormat('Vague {wave}/{waves} · {foes} assaillants', {
        wave: state.wave,
        waves: state.waves,
        foes: state.foes,
      });
  return [wave, present];
}

/** The clock's tooltip. */
export function clockTitle(state: WorldEventState): string {
  if (state.phase === 'running') return uiText('Temps restant');
  if (state.kind === 'convoy') return uiText('Avant le départ');
  if (state.kind === 'giant') return uiText('Avant son arrivée');
  return uiText('Avant l’attaque');
}

/** What to do to enter the ranking. */
export function joinHint(state: WorldEventState): string {
  if (state.kind === 'convoy') return uiText('Reste près du chariot pour entrer au classement');
  if (state.kind === 'giant') return uiText('Frappe le géant pour entrer au classement');
  return uiText('Frappe un assaillant pour entrer au classement');
}

/** The result window's title. */
export function resultTitle(result: WorldEventEnded, eventName: string): string {
  if (result.outcome === 'cancelled') return uiFormat('{event} annulée', { event: eventName });
  if (result.outcome === 'stopped') return uiFormat('{event} interrompue', { event: eventName });
  const won = result.outcome === 'won';
  if (result.kind === 'giant') {
    return won ? uiText('Le géant est tombé !') : uiText('Le géant s’est éloigné');
  }
  if (result.kind === 'convoy') {
    return won
      ? uiFormat('Le chariot est arrivé à {place} !', { place: result.place })
      : uiText('Le chariot n’est pas arrivé à temps');
  }
  return won
    ? uiFormat('{place} est sauvé !', { place: result.place })
    : uiFormat('{place} est pillé', { place: result.place });
}

/** Said to whoever scored nothing. */
export function absentLine(result: WorldEventEnded): string {
  if (result.kind === 'convoy') return uiText('Tu n’as pas pris part à l’escorte.');
  if (result.kind === 'giant') return uiText('Tu n’as pas pris part au combat.');
  return uiText('Tu n’as pas pris part à la défense.');
}

/** The cart's gold, straight into the purse; empty when there is none. */
export function goldLine(result: WorldEventEnded): string {
  const gold = result.reward?.gold ?? 0;
  return gold > 0 ? uiFormat('+{gold} or dans ta bourse', { gold: uiNumber(gold) }) : '';
}
